/* Gotta infinite scroll v3.
 * Strict mode follows the complete verified set's logical pages. Non-strict mode
 * follows Shopify's native pagination. Optional display batches are buffered
 * without changing either page size; stale generations are discarded.
 */
(() => {
  'use strict';
  if (window.GottaInfiniteScroll) { window.GottaInfiniteScroll.reset(); return; }
  let state = null;
  let generation = 0;
  const root = () => document.getElementById('ProductGridContainer');
  const positive = (value, fallback) => Number.isFinite(Number(value)) && Number(value) > 0 ? Math.floor(Number(value)) : fallback;
  const keyOf = (card) => {
    if (card.dataset.productId) return card.dataset.productId;
    const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href');
    if (!href) return '';
    const path = new URL(href, window.location.href).pathname.split('/products/')[1];
    return path || '';
  };
  function stop() {
    generation += 1;
    state?.controller?.abort();
    state?.observer?.disconnect();
    state = null;
  }
  function controls(current) {
    if (!current || current !== state) return;
    const more = Boolean(current.nextURL || current.buffer.length);
    const block = current.root.querySelector('[data-gotta-load-more]');
    const button = block?.querySelector('[data-gotta-load-button]');
    const status = block?.querySelector('[data-gotta-load-status]');
    if (block) block.hidden = !more;
    if (button) {
      button.disabled = current.loading;
      button.textContent = current.error ? 'Reintentar' : (current.loading ? 'Cargando…' : 'Cargar más');
    }
    if (status) status.textContent = current.error ? 'No se pudieron cargar más productos. Puedes reintentar o usar la paginación.' : '';
    current.root.querySelectorAll('[data-gotta-native-pagination]').forEach((pagination) => { pagination.hidden = !current.error; });
    if (!more) current.observer?.disconnect();
  }
  function reset() {
    stop();
    const live = root();
    const grid = live?.querySelector('ul#product-grid');
    if (!live || !grid || live.dataset.gottaNativeGrid !== 'true' || live.dataset.infinite !== 'true') return;
    if (live.getAttribute('aria-busy') === 'true' || live.classList.contains('gotta-facets-failed')) return;
    // Wait for the first normalized, in-stock response before fetching another page.
    if (live.dataset.autoAvailability === 'true' && (live.dataset.stockVersion === '3' ? live.dataset.stockVerified !== 'true' : live.dataset.availabilityApplied !== 'true')) return;
    const block = live.querySelector('[data-gotta-load-more]');
    if (!block) return;
    state = {
      generation, root: live, grid, nextURL: live.dataset.nextUrl || '',
      page: positive(live.dataset.currentPage, 1), batch: positive(live.dataset.batch, positive(live.dataset.initial, 24)),
      buffer: [], seen: new Set(Array.from(grid.children).map(keyOf).filter(Boolean)),
      requested: new Set(), loading: false, error: false, controller: null, observer: null
    };
    const current = state;
    block.querySelector('[data-gotta-load-button]')?.addEventListener('click', () => load(current));
    if ('IntersectionObserver' in window) {
      current.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !current.error) load(current);
      }, { rootMargin: '500px 0px' });
      current.observer.observe(block);
    }
    controls(current);
  }
  function requestURL(nextURL, current) {
    const url = new URL(nextURL, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) throw new Error('Invalid pagination path');
    // Native links contain filters; preserve preview/market context if it was omitted.
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      if (!['page', 'phcursor', 'section_id', 'sections'].includes(key) && !url.searchParams.has(key)) url.searchParams.append(key, value);
    }
    if (window.GottaFacetController) url.search = window.GottaFacetController.normalize(url.search);
    url.searchParams.set('section_id', current.grid.dataset.id);
    url.hash = '';
    return url;
  }
  async function fetchPage(current) {
    const url = requestURL(current.nextURL, current);
    if (current.requested.has(url.toString())) throw new Error('Repeated pagination cursor');
    const controller = new AbortController();
    current.controller = controller;
    const timer = setTimeout(() => controller.abort(), 45000);
    try {
      const strictStock = current.root.dataset.stockVersion === '3' && current.root.dataset.autoAvailability === 'true';
      const logicalParams = new URLSearchParams(url.search);
      logicalParams.delete('section_id');
      const firstURL = new URL(url);
      if (strictStock) { firstURL.searchParams.delete('page'); firstURL.searchParams.delete('phcursor'); }
      const response = await fetch(firstURL.toString(), { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Next page failed (${response.status})`);
      const text = await response.text();
      if (current !== state || current.generation !== generation) return false;
      let parsed = new DOMParser().parseFromString(text, 'text/html');
      if (strictStock) {
        if (!window.GottaStockGrid) throw new Error('Missing v3 stock engine');
        const result = await window.GottaStockGrid.prepare(parsed, logicalParams.toString(), { section: current.grid.dataset.id, signal: controller.signal });
        if (current !== state || current.generation !== generation) return false;
        parsed = result.parsed;
        if (parsed.getElementById('ProductGridContainer').dataset.stockSignature !== current.root.dataset.stockSignature) {
          const changed = new Error('Availability changed. Refreshing results.'); changed.code = 'STOCK_CHANGED'; throw changed;
        }
      }
      const source = parsed.getElementById('ProductGridContainer');
      const grid = parsed.querySelector('ul#product-grid');
      if (!source || !grid || grid.dataset.id !== current.grid.dataset.id) throw new Error('Invalid next-page section');
      const page = positive(source.dataset.currentPage, 0);
      if (page <= current.page) throw new Error('Pagination did not advance');
      if (current.root.dataset.autoAvailability === 'true' && (strictStock ? source.dataset.stockVerified !== 'true' : source.dataset.availabilityApplied !== 'true')) throw new Error('Next page lost availability validation');
      current.requested.add(url.toString());
      current.page = page;
      current.nextURL = source.dataset.nextUrl || '';
      Array.from(grid.children).forEach((card) => {
        const key = keyOf(card);
        if (!key || current.seen.has(key)) return;
        current.seen.add(key);
        current.buffer.push(card);
      });
      return true;
    } finally {
      clearTimeout(timer);
      if (current === state) current.controller = null;
    }
  }
  async function load(current = state) {
    if (!current || current !== state || current.loading || (!current.nextURL && !current.buffer.length)) return false;
    current.loading = true;
    current.error = false;
    controls(current);
    try {
      // Fetch only enough consecutive verified/native pages to satisfy this display batch.
      while (current.buffer.length < current.batch && current.nextURL) {
        if (!(await fetchPage(current))) return false;
        if (current !== state || current.generation !== generation) return false;
      }
      if (current !== state || !current.grid.isConnected) return false;
      const fragment = document.createDocumentFragment();
      const cards = current.buffer.splice(0, current.batch);
      cards.forEach((card) => {
        card.classList.add('scroll-trigger--cancel');
        card.querySelectorAll('script:not([type="application/json"])').forEach((script) => script.remove());
        fragment.appendChild(card);
      });
      current.grid.appendChild(fragment);
      if (window.GottaFacetController) window.GottaFacetController.refreshCards(current.root);
      document.dispatchEvent(new CustomEvent('gotta:products:appended', { detail: { count: cards.length } }));
      return true;
    } catch (error) {
      if (current !== state || current.generation !== generation) return false;
      if (error.code === 'STOCK_CHANGED' && window.GottaFacetController) {
        const params = new URLSearchParams(window.location.search); params.delete('page');
        window.GottaFacetController.renderPage(params.toString(), null, false, { replaceURL: true });
        return false;
      }
      current.error = true;
      current.observer?.disconnect();
      console.warn('[Gotta infinite scroll]', error.message);
      return false;
    } finally {
      if (current === state) {
        current.loading = false;
        controls(current);
      }
    }
  }
  window.GottaInfiniteScroll = { reset, stop, load };
  document.addEventListener('gotta:facets:loading', stop);
  document.addEventListener('gotta:facets:rendered', reset);
  document.addEventListener('gotta:facets:ready', reset);
  document.addEventListener('shopify:section:load', reset);
  document.addEventListener('shopify:section:unload', (event) => { if (state && event.target.contains(state.root)) stop(); });
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', (event) => { if (event.persisted) reset(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reset, { once: true });
  else reset();
})();
